import React, { useMemo, useState } from "react";
import "./vehicleManagementCss.css";
// Lightweight primitives
const HeaderCell = ({ children, style, className }) => (
  <div
    className={className}
    style={{
      fontSize: "14px",
      fontWeight: 700,
      lineHeight: "18px",
      fontFamily: "Verizon-NHG-eDS",
      letterSpacing: "0.5px",
      padding: "8px 12px",
      borderBottom: "1px solid #d8dada",
      ...style,
    }}
  >
    {children}
  </div>
);
const RowCell = ({ children, style, className }) => (
  <div className={className} style={{ padding: "8px 12px", display: "flex", alignItems: "center", ...style }}>
    {children}
  </div>
);
const Row = ({ children, expanded }) => <div style={{ borderBottom: "1px solid #eee", background: expanded ? "#fafafa" : "#fff" }}>{children}</div>;

export default function NewGridComponent({
  // data
  rows = [], // array of objects; special rows marked with isFullRow: true or id === "full-row"
  columns = [], // [{ value, key, width?, headerStyle?, cellStyle?, headerClassName?, cellClassName?, renderHeader?(), renderCell?(row, value), renderColumnWrapper?(content, row, col) }]
  // selection
  isSelectionEnabled = true,
  selectionMode = "multiple", // 'single' | 'multiple'
  onSelectionChange, // (selectedRows) => void
  // expansion
  enableRowExpansion = true,
  rowExpansionTemplate, // (row) => ReactNode (only for normal rows)
  rowExpansionPosition = "end", // 'start' | 'end'
  onRowExpand, // (row) => void
  // special full-width row
  renderFullRow, // (row) => ReactNode; for rows with isFullRow === true
  // header-level bulk selection
  enableBulkSelection = true,
  // search
  enableGlobalSearch = false,
  globalSearchPlaceholder = "Search...",
  globalSearchFields = [], // defaults to columns.value
  // paginator
  paginator = false,
  rowsPerPageOptions = [10, 15, 20],
  defaultRowsPerPage = 10,
  // actions column
  rowActions, // (row) => ReactNode (normal rows)
  // misc
  noDataMessage = "No data to display",
  // NEW: stable id for persisting selection across unmount (ex: accordion collapse)
  gridId,
}) {
  const safeRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);
  const safeCols = useMemo(() => (Array.isArray(columns) ? columns : []), [columns]);

  const [selectedMap, setSelectedMap] = useState(() => new Map());
  const [expandedMap, setExpandedMap] = useState(() => new Map());
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  const isFullRow = (row) => row?.isFullRow === true || row?.id === "full-row";

  const getRowId = (row, index) => {
    if (row && (row.id || row.key)) return row.id ?? row.key;
    return `row-${index}`;
  };

  const searchFields = useMemo(
    () => (globalSearchFields.length ? globalSearchFields : safeCols.map((c) => c.value).filter(Boolean)),
    [safeCols, globalSearchFields]
  );

  const filteredRows = useMemo(() => {
    if (!enableGlobalSearch || !search.trim()) return safeRows;
    const q = search.toLowerCase();
    return safeRows.filter((r) =>
      isFullRow(r)
        ? true // include full rows in filtered output
        : searchFields.some((f) => {
            const v = r?.[f];
            return v != null && String(v).toLowerCase().includes(q);
          })
    );
  }, [safeRows, search, enableGlobalSearch, searchFields]);

  const pagedRows = useMemo(() => {
    if (!paginator) return filteredRows;
    const start = page * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, paginator, page, rowsPerPage]);

  const visibleSelectableIds = useMemo(() => {
    const basis = paginator ? pagedRows : filteredRows;
    return basis.filter((r) => !isFullRow(r)).map((r, idx) => getRowId(r, idx));
  }, [pagedRows, filteredRows, paginator]);

  const allChecked = useMemo(() => {
    if (!enableBulkSelection || !isSelectionEnabled || selectionMode === "single") return false;
    if (!visibleSelectableIds.length) return false;
    return visibleSelectableIds.every((id) => selectedMap.has(id));
  }, [enableBulkSelection, isSelectionEnabled, selectionMode, visibleSelectableIds, selectedMap]);

  const anyChecked = useMemo(() => selectedMap.size > 0, [selectedMap]);

  const toggleRow = (row, index) => {
    if (isFullRow(row) || !isSelectionEnabled) return;
    const id = getRowId(row, index);
    setSelectedMap((m) => {
      const next = new Map(m);
      const isSelected = next.has(id);
      if (selectionMode === "single") {
        next.clear();
        if (!isSelected) next.set(id, row);
      } else {
        if (isSelected) next.delete(id);
        else next.set(id, row);
      }
      onSelectionChange?.(Array.from(next.values()));
      return next;
    });
  };

  const bulkToggle = (checked) => {
    if (!enableBulkSelection || !isSelectionEnabled) return;
    setSelectedMap(() => {
      const next = new Map();
      if (checked) {
        const basis = paginator ? pagedRows : filteredRows;
        basis.forEach((r, idx) => {
          if (!isFullRow(r)) next.set(getRowId(r, idx), r);
        });
      }
      onSelectionChange?.(Array.from(next.values()));
      return next;
    });
  };

  const toggleExpand = (row, index) => {
    if (!enableRowExpansion || isFullRow(row)) return;
    const id = getRowId(row, index);
    setExpandedMap((m) => {
      const next = new Map();
      const willExpand = !m.get(id);
      if (willExpand) {
        next.set(id, true); // Only expand the clicked row
        if (typeof onRowExpand === "function") {
          onRowExpand(row);
        }
      }
      // If collapsing, all rows will be collapsed (next is empty)
      return next;
    });
  };

  // Compute the shared grid template columns string (used by both header and rows)
  const gridTemplateColumns = useMemo(() => {
    const baseCols = safeCols.map((c) => (c.width ? `minmax(${c.minWidth || "100px"}, ${c.width})` : "1fr"));
    const checkboxCol = isSelectionEnabled ? ["40px"] : [];
    const expanderStartCol = rowExpansionPosition === "start" && enableRowExpansion ? ["40px"] : [];
    const hasEndActionsCol = rowActions || (rowExpansionPosition === "end" && enableRowExpansion);
    const endActionsCol = hasEndActionsCol ? ["100px"] : [];
    const cols = [];
    cols.push(...expanderStartCol);
    if (isSelectionEnabled) cols.push(...checkboxCol);
    cols.push(...baseCols);
    cols.push(...endActionsCol);
    return cols.join(" ");
  }, [safeCols, isSelectionEnabled, rowExpansionPosition, enableRowExpansion, rowActions]);

  const storageKey = gridId ? `NewGridComponent:selected:${gridId}` : null;

  // Hydrate persisted selection on mount/remount
  React.useEffect(() => {
    if (!storageKey) return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return;

      setSelectedMap(() => {
        const next = new Map();
        // parsed is array of [id, row] entries
        for (const entry of parsed) {
          if (Array.isArray(entry) && entry.length === 2) {
            next.set(entry[0], entry[1]);
          }
        }
        // notify parent on hydrate
        onSelectionChange?.(Array.from(next.values()));
        return next;
      });
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Persist selection whenever it changes
  React.useEffect(() => {
    if (!storageKey) return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(Array.from(selectedMap.entries())));
    } catch {
      // ignore
    }
  }, [storageKey, selectedMap]);

  // When selection is turned off by parent (ex: closing bulk action bar), clear any existing selections.
  React.useEffect(() => {
    if (!isSelectionEnabled && selectedMap.size > 0) {
      setSelectedMap(() => new Map());
      // Notify parent once (guarded by size check to avoid loops)
      onSelectionChange?.([]);
    }
  }, [isSelectionEnabled, selectedMap.size, onSelectionChange]);
  return (
    <div style={{ border: "1px solid #d8dada", borderRadius: 8, overflow: "hidden", color: "black", fontFamily: "Verizon-NHG-eDS" }}>
      {/* Header row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: gridTemplateColumns,
        }}
      >
        {rowExpansionPosition === "start" && enableRowExpansion && <HeaderCell />}

        {isSelectionEnabled && enableBulkSelection ? (
          <HeaderCell>
            <div className="custom-checkbox">
              <input type="checkbox" aria-label="select-all" checked={allChecked} onChange={(e) => bulkToggle(e.target.checked)} />
            </div>
          </HeaderCell>
        ) : (
          isSelectionEnabled && <HeaderCell />
        )}

        {safeCols.map((col, colIndex) => (
          <HeaderCell
            key={col.value ?? col.key}
            style={{ width: col.width, ...(col.headerStyle || {}), borderRight: colIndex < safeCols.length - 1 ? "1px solid #DDDAD4" : "none" }}
            className={col.headerClassName}
          >
            {col.renderHeader ? col.renderHeader() : col.key ?? col.value}
          </HeaderCell>
        ))}
        {(rowActions || (rowExpansionPosition === "end" && enableRowExpansion)) && (
          <HeaderCell style={{ textAlign: "center", whiteSpace: "nowrap", borderLeft: "1px solid #DDDAD4" }}>Actions</HeaderCell>
        )}
      </div>

      {/* Global search */}
      {enableGlobalSearch && (
        <div style={{ padding: "8px 12px", borderBottom: "1px solid #eee", display: "flex", gap: 8 }}>
          <input
            style={{ flex: 1, padding: "6px 8px", border: "1px solid #ccc", borderRadius: 4 }}
            placeholder={globalSearchPlaceholder}
            value={search}
            onChange={(e) => {
              setPage(0);
              setSearch(e.target.value);
            }}
          />
          {anyChecked && <span style={{ fontSize: 12, color: "#6f7171" }}>{selectedMap.size} selected</span>}
        </div>
      )}

      {/* No data */}
      {!pagedRows.length && <div style={{ padding: 12, color: "#6f7171" }}>{noDataMessage}</div>}

      {/* Rows */}
      {pagedRows.map((row, rowIndex) => {
        const id = getRowId(row, rowIndex);
        const expanded = !!expandedMap.get(id);
        const full = isFullRow(row);

        // Full-width row: render custom design only, no grid cells
        if (full) {
          return (
            <div key={id} style={{ borderBottom: "1px solid #eee", background: "#fff" }}>
              <div>{typeof renderFullRow === "function" ? renderFullRow(row) : <span>Provide renderFullRow to customize this row.</span>}</div>
            </div>
          );
        }

        // Normal row
        return (
          <div key={id}>
            <Row expanded={expanded}>
              {" "}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: gridTemplateColumns,
                  alignItems: "stretch",
                }}
              >
                {rowExpansionPosition === "start" && enableRowExpansion && (
                  <RowCell>
                    <button
                      aria-label="expand-row"
                      onClick={() => toggleExpand(row, rowIndex)}
                      style={{ border: "none", background: "transparent", cursor: "pointer" }}
                    >
                      {expanded ? "▾" : "▸"}
                    </button>
                  </RowCell>
                )}
                {isSelectionEnabled && (
                  <RowCell style={{ background: "#F8F7F5" }}>
                    <div className="custom-checkbox">
                      <input type="checkbox" aria-label={`select-${id}`} checked={selectedMap.has(id)} onChange={() => toggleRow(row, rowIndex)} />
                    </div>
                  </RowCell>
                )}
                {safeCols.map((col, colIndex) => {
                  const raw = row?.[col.value];
                  const content = col.renderCell ? col.renderCell(row, col.value, { isSelectionEnabled }) : raw;
                  const isReactNode = React.isValidElement(content);
                  const display = isReactNode ? content : content != null ? String(content) : "--";

                  // Optional wrapper to apply custom design around the cell
                  const maybeWrapped = col.renderColumnWrapper
                    ? col.renderColumnWrapper(isReactNode ? content : <span>{display}</span>, row, col)
                    : isReactNode
                    ? content
                    : display;

                  return (
                    <RowCell
                      key={`${id}-${col.value ?? col.key}`}
                      style={{
                        ...col.cellStyle,
                        borderRight: colIndex < safeCols.length - 1 ? "1px solid #DDDAD4" : "none",
                      }}
                      className={col.cellClassName}
                    >
                      {maybeWrapped}
                    </RowCell>
                  );
                })}
                {/* Combined Actions cell: moreIcon | chevron */}
                {(rowActions || (rowExpansionPosition === "end" && enableRowExpansion)) && (
                  <RowCell
                    style={{
                      padding: "8px 8px",
                      background: "#F8F7F5",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 0,
                      borderLeft: "1px solid #DDDAD4",
                    }}
                  >
                    {rowActions && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                        {rowActions(row, { isSelectionEnabled })}
                      </div>
                    )}
                    {rowActions && rowExpansionPosition === "end" && enableRowExpansion && (
                      <div
                        style={{
                          width: "1px",
                          alignSelf: "stretch",
                          margin: "4px 0",
                          borderRight: "1px solid #DDDAD4",
                        }}
                      />
                    )}
                    {rowExpansionPosition === "end" && enableRowExpansion && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1 }}>
                        <button
                          aria-label="expand-row"
                          onClick={() => toggleExpand(row, rowIndex)}
                          style={{ border: "none", background: "transparent", cursor: "pointer" }}
                          disabled={isSelectionEnabled}
                        >
                          <i className={expanded ? "pi pi-chevron-up" : "pi pi-chevron-down"}></i>
                        </button>
                      </div>
                    )}
                  </RowCell>
                )}
              </div>
            </Row>

            {enableRowExpansion && expanded && <div style={{ padding: "8px 0" }}>{rowExpansionTemplate?.(row, gridTemplateColumns)}</div>}
          </div>
        );
      })}

      {/* Paginator */}
      {paginator && filteredRows.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderTop: "1px solid #eee" }}>
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(p - 1, 0))}
            disabled={page === 0}
            style={{ border: "1px solid #ccc", padding: "4px 6px", borderRadius: 4 }}
          >
            ◂
          </button>
          <span style={{ fontSize: 12, color: "#6f7171" }}>
            Page {page + 1} of {Math.ceil(filteredRows.length / rowsPerPage)}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => (p + 1 < Math.ceil(filteredRows.length / rowsPerPage) ? p + 1 : p))}
            disabled={page + 1 >= Math.ceil(filteredRows.length / rowsPerPage)}
            style={{ border: "1px solid #ccc", padding: "4px 6px", borderRadius: 4 }}
          >
            ▸
          </button>
          <span style={{ marginLeft: "auto", fontSize: 12 }}>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={(e) => {
              setPage(0);
              setRowsPerPage(Number(e.target.value));
            }}
          >
            {rowsPerPageOptions.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
